import { Pencil } from 'lucide-react';

import type { VenueDetail } from '../../../../api/venues';

import { CoMarketingSummaryForm } from './CoMarketingSummaryForm';
import { CoMarketingSummaryView } from './CoMarketingSummaryView';
import { useSummaryEditor } from './use-summary-editor';

interface CoMarketingSummaryProps {
  venue: VenueDetail;
  /** Whether the server lets this caller edit the venue's marketing. */
  canEdit: boolean;
}

const isUnassessed = (v: VenueDetail) =>
  v.comarketingRating === null && v.followingOnInstagram === null && !v.hubspotUrl;

const emptyStyle = {
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px dashed var(--nb-border-2)',
  fontSize: 12,
  color: 'var(--nb-muted)',
  lineHeight: 1.5,
} as const;

/** Partner summary with its own inline edit: rating, Instagram follow state, HubSpot link. */
export function CoMarketingSummary({ venue, canEdit }: CoMarketingSummaryProps) {
  const editor = useSummaryEditor(venue);
  const unassessed = isUnassessed(venue);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="nb-eyebrow" style={{ fontSize: 10 }}>
          Partner summary
        </span>
        <span style={{ flex: 1 }} />
        {canEdit && !editor.editing && (
          <button
            type="button"
            ref={editor.editButtonRef}
            className="nb-btn nb-btn-ghost"
            onClick={editor.start}
            style={{ height: 22, fontSize: 11, padding: '0 8px' }}
          >
            <Pencil size={11} aria-hidden="true" />
            {unassessed ? 'Assess' : 'Edit'}
          </button>
        )}
      </div>

      {editor.editing && (
        <CoMarketingSummaryForm
          draft={editor.draft}
          onChange={editor.change}
          errors={editor.errors}
          message={editor.message}
          saving={editor.saving}
          onSave={() => void editor.save()}
          onCancel={editor.cancel}
        />
      )}
      {!editor.editing && unassessed && (
        <div style={emptyStyle}>
          Not assessed yet.{' '}
          {canEdit
            ? 'Rate the partnership, note whether we follow them, and link HubSpot.'
            : 'Nobody has rated this venue.'}
        </div>
      )}
      {!editor.editing && !unassessed && <CoMarketingSummaryView values={venue} />}
    </div>
  );
}
